/**
 * 归一化流程图节点。
 *
 * @param {Array} nodes 节点列表
 * @returns {Array}
 */
export function normalizeFlowNodes(nodes) {
  return (nodes || []).map((node) => ({
    bpmnUserTaskId: node.bpmnUserTaskId || "",
    groupKey: node.groupKey || "",
    subProcessInstanceId: node.subProcessInstanceId || "",
    subProcessSource: Boolean(node.subProcessSource),
    ...node,
  }));
}

/**
 * 归一化流程图边。
 *
 * @param {Array} edges 边列表
 * @returns {Array}
 */
export function normalizeFlowEdges(edges) {
  return (edges || []).map((edge) => ({
    historyCount: edge.historyCount || 1,
    edgeType: edge.edgeType || "forward",
    edgeName: edge.edgeName || "",
    businessKey: edge.businessKey || "",
    groupKey: edge.groupKey || "",
    subProcessInstanceId: edge.subProcessInstanceId || "",
    bridgeType: edge.bridgeType || "",
    routeSemantic: edge.routeSemantic || "",
    ...edge,
  }));
}

/**
 * 获取节点分组键。
 *
 * @param {Object} node 节点
 * @returns {string}
 */
export function getNodeGroupKey(node) {
  if (!node) {
    return "";
  }
  return node.subProcessInstanceId || node.groupKey || "";
}

/**
 * 比较两个节点顺序。
 *
 * @param {Object} leftNode 左节点
 * @param {Object} rightNode 右节点
 * @returns {number}
 */
export function compareFlowNodes(leftNode, rightNode) {
  const leftProcessWeight = leftNode.processType === "main" ? 0 : 1;
  const rightProcessWeight = rightNode.processType === "main" ? 0 : 1;
  if (leftProcessWeight !== rightProcessWeight) {
    return leftProcessWeight - rightProcessWeight;
  }
  const leftOrderNum = Number(leftNode.orderNum || 0);
  const rightOrderNum = Number(rightNode.orderNum || 0);
  if (leftOrderNum !== rightOrderNum) {
    return leftOrderNum - rightOrderNum;
  }
  return (leftNode.nodeId || "").localeCompare(rightNode.nodeId || "");
}

/**
 * 构建深度信息。
 *
 * @param {Array} nodes 节点列表
 * @param {Array} edges 边列表
 * @returns {{nodeDepths:Object,columns:Object,adjacency:Object}}
 */
export function buildFlowDepthInfo(nodes, edges) {
  const nodeDepths = {};
  const indegreeMap = {};
  const depthAdjacency = {};

  nodes.forEach((node) => {
    indegreeMap[node.nodeId] = 0;
    depthAdjacency[node.nodeId] = [];
  });

  edges.forEach((edge) => {
    if (!depthAdjacency[edge.from]) {
      depthAdjacency[edge.from] = [];
    }
    depthAdjacency[edge.from].push(edge.to);
    indegreeMap[edge.to] = (indegreeMap[edge.to] || 0) + 1;
  });

  const zeroIndegreeNodes = nodes
    .filter((node) => !indegreeMap[node.nodeId])
    .sort(compareFlowNodes)
    .map((node) => node.nodeId);
  const queue = [...zeroIndegreeNodes];
  const mutableIndegreeMap = { ...indegreeMap };

  zeroIndegreeNodes.forEach((nodeId) => {
    nodeDepths[nodeId] = 0;
  });

  while (queue.length > 0) {
    const currentNodeId = queue.shift();
    const nextNodeIds = depthAdjacency[currentNodeId] || [];
    nextNodeIds.forEach((nextNodeId) => {
      const nextDepth = (nodeDepths[currentNodeId] || 0) + 1;
      nodeDepths[nextNodeId] = Math.max(nodeDepths[nextNodeId] || 0, nextDepth);
      mutableIndegreeMap[nextNodeId] = Math.max(
        (mutableIndegreeMap[nextNodeId] || 0) - 1,
        0
      );
      if (mutableIndegreeMap[nextNodeId] === 0) {
        queue.push(nextNodeId);
      }
    });
  }

  [...nodes].sort(compareFlowNodes).forEach((node) => {
    if (nodeDepths[node.nodeId] !== undefined) {
      return;
    }
    const parentDepths = edges
      .filter((edge) => edge.to === node.nodeId)
      .map((edge) => nodeDepths[edge.from])
      .filter((depth) => depth !== undefined);
    nodeDepths[node.nodeId] =
      parentDepths.length > 0 ? Math.max(...parentDepths) + 1 : 0;
  });

  const columns = {};
  nodes.forEach((node) => {
    const depth = nodeDepths[node.nodeId] || 0;
    if (!columns[depth]) {
      columns[depth] = [];
    }
    columns[depth].push(node);
  });

  return {
    nodeDepths,
    columns,
    adjacency: depthAdjacency,
  };
}

/**
 * 组装子流程分组。
 *
 * @param {Object} params 参数
 * @returns {Array}
 */
export function buildSubProcessGroups({
  flowNodes,
  enterBridgeEdges,
  validEdges,
  nodeMap,
}) {
  const groups = {};
  (enterBridgeEdges || []).forEach((edge) => {
    const groupKey =
      edge.subProcessInstanceId || edge.groupKey || edge.businessKey || edge.to;
    if (!groups[groupKey]) {
      groups[groupKey] = {
        businessKey: groupKey,
        groupKey,
        sourceNodeId: edge.from,
        enterEdge: edge,
        nodes: [],
        forwardEdges: [],
      };
    }
  });

  (flowNodes || []).forEach((node) => {
    if (node.processType !== "sub") {
      return;
    }
    const groupKey = getNodeGroupKey(node);
    if (!groupKey || !groups[groupKey]) {
      return;
    }
    groups[groupKey].nodes.push(node);
  });

  (validEdges || []).forEach((edge) => {
    const groupKey =
      edge.subProcessInstanceId || edge.groupKey || edge.businessKey;
    if (edge.edgeType !== "forward" || !groupKey || !groups[groupKey]) {
      return;
    }
    const fromNode = nodeMap[edge.from];
    const toNode = nodeMap[edge.to];
    if (
      fromNode &&
      toNode &&
      fromNode.processType === "sub" &&
      toNode.processType === "sub"
    ) {
      groups[groupKey].forwardEdges.push(edge);
    }
  });

  return Object.values(groups).filter((group) => group.nodes.length > 0);
}

/**
 * 构建发起节点到子流程分组映射。
 *
 * @param {Array} groups 子流程分组
 * @returns {Object}
 */
export function buildSubProcessSourceGroupMap(groups) {
  const sourceGroupMap = {};
  (groups || []).forEach((group) => {
    if (!sourceGroupMap[group.sourceNodeId]) {
      sourceGroupMap[group.sourceNodeId] = [];
    }
    sourceGroupMap[group.sourceNodeId].push(group);
  });
  Object.keys(sourceGroupMap).forEach((sourceNodeId) => {
    sourceGroupMap[sourceNodeId].sort((left, right) => {
      const leftFirstNode = [...left.nodes].sort(compareFlowNodes)[0];
      const rightFirstNode = [...right.nodes].sort(compareFlowNodes)[0];
      return compareFlowNodes(leftFirstNode || {}, rightFirstNode || {});
    });
  });
  return sourceGroupMap;
}
